import { Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { formatAddress } from './addressFormatter';

/**
 * Service de géolocalisation centralisé pour l'application AgenTime
 * Gère la géolocalisation, le calcul de distances et la validation de proximité
 */

// Configuration par défaut
const DEFAULT_CONFIG = {
  checkInRadius: 100, // Rayon de pointage par défaut en mètres
  timeout: 10000, // Timeout de la géolocalisation en ms
  maximumAge: 30000, // Âge maximum des données de position en ms
  enableHighAccuracy: true, // Activation de la haute précision
  maxRetries: 3, // Nombre maximum de tentatives
  retryDelay: 1000, // Délai entre les tentatives en ms
};

// Cache pour les coordonnées géocodées
const geocodingCache = new Map();

// Cache pour la configuration de géolocalisation
let geolocationConfigCache = null;

/**
 * Récupère la configuration de géolocalisation depuis Firestore
 * @returns {Promise<Object>} Configuration de géolocalisation
 */
export const getGeolocationConfig = async () => {
  // Utiliser le cache si disponible
  if (geolocationConfigCache) {
    console.log('Utilisation du cache de configuration géolocation:', geolocationConfigCache.checkInRadius);
    return geolocationConfigCache;
  }

  try {
    console.log('Récupération de la configuration depuis Firestore...');
    const configDoc = await getDoc(doc(db, 'settings', 'general'));
    
    if (configDoc.exists()) {
      const configData = configDoc.data();
      console.log('Configuration Firestore trouvée:', configData);
      
      // Fusionner avec la configuration par défaut et convertir les valeurs
      const mergedConfig = { 
        ...DEFAULT_CONFIG, 
        ...configData,
        checkInRadius: Number(configData.checkInRadius) || DEFAULT_CONFIG.checkInRadius
      };
      geolocationConfigCache = mergedConfig;
      console.log('Configuration fusionnée:', mergedConfig.checkInRadius);
      return mergedConfig;
    } else {
      // Retourner la configuration par défaut si aucun document n'existe
      console.log('Aucune configuration Firestore trouvée, utilisation de la valeur par défaut');
      geolocationConfigCache = DEFAULT_CONFIG;
      return DEFAULT_CONFIG;
    }
  } catch (error) {
    console.error('Erreur lors de la récupération de la configuration de géolocalisation:', error);
    // Retourner la configuration par défaut en cas d'erreur
    return DEFAULT_CONFIG;
  }
};

/**
 * Récupère le rayon de pointage configuré depuis Firestore
 * @returns {Promise<number>} Rayon de pointage en mètres
 */
export const getCheckInRadius = async () => {
  const config = await getGeolocationConfig();
  // Convertir en nombre (au cas où la valeur serait sauvegardée comme chaîne)
  const radius = Number(config.checkInRadius);
  console.log('getCheckInRadius retourne:', radius);
  return radius;
};

/**
 * Invalide le cache de configuration (utile pour les tests ou les mises à jour)
 */
export const invalidateGeolocationConfigCache = () => {
  geolocationConfigCache = null;
};

/**
 * Calcule la distance entre deux points GPS en utilisant la formule Haversine
 * @param {number} lat1 - Latitude du premier point
 * @param {number} lon1 - Longitude du premier point
 * @param {number} lat2 - Latitude du second point
 * @param {number} lon2 - Longitude du second point
 * @returns {number} Distance en mètres
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  // Validation des coordonnées
  if (!isValidCoordinate(lat1, lon1) || !isValidCoordinate(lat2, lon2)) {
    console.warn('Coordonnées invalides pour le calcul de distance');
    return Infinity;
  }

  const R = 6371e3; // Rayon de la Terre en mètres
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  return R * c; // Distance en mètres
};

/**
 * Vérifie si des coordonnées GPS sont valides
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {boolean} True si les coordonnées sont valides
 */
export const isValidCoordinate = (lat, lng) => {
  return (
    typeof lat === 'number' && 
    typeof lng === 'number' &&
    !isNaN(lat) && 
    !isNaN(lng) &&
    lat >= -90 && 
    lat <= 90 &&
    lng >= -180 && 
    lng <= 180
  );
};

/**
 * Obtient la position actuelle de l'utilisateur avec gestion d'erreurs avancée
 * @param {Object} options - Options de géolocalisation
 * @returns {Promise<Object>} Objet contenant les coordonnées et les métadonnées
 */
export const getCurrentPosition = async (options = {}) => {
  const config = { ...DEFAULT_CONFIG, ...options };
  
  if (!('geolocation' in navigator)) {
    throw new Error('Géolocalisation non supportée par le navigateur');
  }

  for (let attempt = 1; attempt <= config.maxRetries; attempt++) {
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            timeout: config.timeout,
            maximumAge: config.maximumAge,
            enableHighAccuracy: config.enableHighAccuracy,
          }
        );
      });

      return {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
        altitude: position.coords.altitude,
        altitudeAccuracy: position.coords.altitudeAccuracy,
        heading: position.coords.heading,
        speed: position.coords.speed,
        timestamp: position.timestamp,
        attempt: attempt,
      };

    } catch (error) {
      if (attempt === config.maxRetries) {
        throw enhanceGeolocationError(error);
      }
      
      // Attendre avant de réessayer
      await new Promise(resolve => setTimeout(resolve, config.retryDelay));
    }
  }
};

/**
 * Charge l'API Google Maps dynamiquement
 * @param {string} apiKey - Clé API Google Maps
 * @returns {Promise<void>}
 */
const loadGoogleMapsAPI = (apiKey) => {
  return new Promise((resolve, reject) => {
    // Vérifier si l'API est déjà chargée
    if (window.google && window.google.maps && window.google.maps.Geocoder) {
      resolve();
      return;
    }

    // Vérifier si le script est déjà en cours de chargement
    const existingScript = document.querySelector('script[data-gmaps]');
    if (existingScript) {
      // Attendre que le script soit chargé
      const checkLoaded = () => {
        if (window.google && window.google.maps && window.google.maps.Geocoder) {
          resolve();
        } else {
          setTimeout(checkLoaded, 100);
        }
      };
      checkLoaded();
      return;
    }

    // Créer et charger le script
    const script = document.createElement('script');
    script.setAttribute('data-gmaps', '1');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geocoding&v=weekly&loading=async`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      // Vérifier que l'API est complètement chargée
      const checkAPI = () => {
        if (window.google && window.google.maps && window.google.maps.Geocoder) {
          resolve();
        } else {
          setTimeout(checkAPI, 100);
        }
      };
      checkAPI();
    };
    
    script.onerror = (error) => {
      reject(new Error('Erreur lors du chargement de Google Maps API'));
    };
    
    document.head.appendChild(script);
  });
};

/**
 * Améliore les messages d'erreur de géolocalisation
 * @param {Error} error - Erreur originale
 * @returns {Error} Erreur enrichie
 */
const enhanceGeolocationError = (error) => {
  const errorMap = {
    [error.PERMISSION_DENIED]: 'Permission de géolocalisation refusée',
    [error.POSITION_UNAVAILABLE]: 'Position indisponible',
    [error.TIMEOUT]: 'Timeout de la géolocalisation',
  };

  const message = errorMap[error.code] || 'Erreur de géolocalisation inconnue';
  
  return new Error(`${message}. Code: ${error.code}`);
};

/**
 * Vérifie si un point est dans un rayon donné autour d'une position cible
 * @param {Object} currentPosition - Position actuelle {lat, lng}
 * @param {Object} targetPosition - Position cible {lat, lng}
 * @param {number} radius - Rayon en mètres
 * @returns {boolean} True si dans le rayon
 */
export const isWithinRadius = (currentPosition, targetPosition, radius) => {
  if (!currentPosition || !targetPosition) return false;
  
  const distance = calculateDistance(
    currentPosition.lat,
    currentPosition.lng,
    targetPosition.lat,
    targetPosition.lng
  );
  
  return distance <= radius;
};

/**
 * Convertit une adresse en coordonnées GPS (géocodage)
 * @param {string|object} address - Adresse à géocoder (chaîne ou objet structuré)
 * @param {string} apiKey - Clé API Google Maps (optionnelle)
 * @returns {Promise<Object>} Coordonnées {lat, lng}
 */
export const geocodeAddress = async (address, apiKey = null) => {
  if (!address) {
    throw new Error('Adresse requise pour le géocodage');
  }

  // Formater l'adresse pour obtenir une chaîne cohérente
  const formattedAddress = formatAddress(address);
  
  // Vérifier le cache d'abord
  const cacheKey = formattedAddress.toLowerCase().trim();
  if (geocodingCache.has(cacheKey)) {
    return geocodingCache.get(cacheKey);
  }

  try {
    // Récupérer la clé API depuis Firestore si non fournie
    let finalApiKey = apiKey;
    if (!finalApiKey) {
      const config = await getGeolocationConfig();
      finalApiKey = config.googleMapsApiKey;
    }

    // Si une clé API est disponible, utiliser Google Maps JavaScript API
    if (finalApiKey) {
      // Vérifier si l'API Google Maps est chargée
      if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
        // Charger l'API Google Maps dynamiquement
        await loadGoogleMapsAPI(finalApiKey);
      }

      const geocoder = new window.google.maps.Geocoder();
      return new Promise((resolve, reject) => {
        geocoder.geocode({ address: formattedAddress }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const loc = results[0].geometry.location;
            // Retourner UNIQUEMENT les coordonnées lat/lng
            const result = { lat: loc.lat(), lng: loc.lng() };
            
            // Mettre en cache le résultat
            geocodingCache.set(cacheKey, result);
            resolve(result);
          } else {
            reject(new Error(`Erreur de géocodage: ${status}`));
          }
        });
      });
    } else {
      // Pas de clé API configurée
      throw new Error('Clé API Google Maps non configurée. Impossible de géocoder l\'adresse.');
    }
  } catch (error) {
    console.error('Erreur lors du géocodage:', error);
    throw new Error(`Impossible de géocoder l'adresse: ${formattedAddress}`);
  }
};

/**
 * Formate une distance en unités lisibles
 * @param {number} distance - Distance en mètres
 * @returns {string} Distance formatée
 */
export const formatDistance = (distance) => {
  if (distance < 1000) {
    return `${Math.round(distance)} m`;
  } else {
    return `${(distance / 1000).toFixed(1)} km`;
  }
};

/**
 * Crée un objet de pointage GPS pour Firestore
 * @param {Object} position - Position GPS
 * @param {string} method - Méthode de pointage ('gps', 'nfc', 'manual')
 * @param {string} appointmentId - ID du rendez-vous
 * @returns {Object} Données de pointage structurées
 */
export const createCheckInData = (position, method = 'gps', appointmentId = null) => {
  return {
    checkedIn: true,
    checkInTime: Timestamp.now(),
    checkInMethod: method,
    checkInLocation: {
      lat: position.lat,
      lng: position.lng,
      accuracy: position.accuracy,
      timestamp: position.timestamp,
    },
    appointmentId: appointmentId,
    metadata: {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      timestamp: Date.now(),
    }
  };
};

/**
 * Vérifie et prépare les données pour un pointage GPS
 * @param {Object} currentPosition - Position actuelle
 * @param {Object} targetPosition - Position cible
 * @param {number} radius - Rayon autorisé en mètres
 * @returns {Object} Résultat de la validation
 */
export const validateCheckIn = (currentPosition, targetPosition, radius) => {
  const isValidCurrent = isValidCoordinate(currentPosition.lat, currentPosition.lng);
  const isValidTarget = isValidCoordinate(targetPosition.lat, targetPosition.lng);
  
  if (!isValidCurrent || !isValidTarget) {
    return {
      valid: false,
      error: 'Coordonnées GPS invalides',
      distance: null,
    };
  }

  const distance = calculateDistance(
    currentPosition.lat,
    currentPosition.lng,
    targetPosition.lat,
    targetPosition.lng
  );

  const withinRadius = distance <= radius;

  return {
    valid: withinRadius,
    distance: distance,
    withinRadius: withinRadius,
    formattedDistance: formatDistance(distance),
    error: withinRadius ? null : `Vous êtes à ${formatDistance(distance)} du client. La distance maximale autorisée est ${formatDistance(radius)}.`,
  };
};

// Export des constantes de configuration
export const GEOLOCATION_CONFIG = DEFAULT_CONFIG;

// Export nommé pour éviter l'export anonyme
const geolocationService = {
  calculateDistance,
  getCurrentPosition,
  isWithinRadius,
  geocodeAddress,
  formatDistance,
  createCheckInData,
  validateCheckIn,
  isValidCoordinate,
  getGeolocationConfig,
  getCheckInRadius,
  invalidateGeolocationConfigCache,
  GEOLOCATION_CONFIG,
};

export default geolocationService;
